import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Brain, User } from 'lucide-react';

const GOALS = ['Weight Loss', 'Muscle Gain', 'Endurance', 'Strength', 'Recovery', 'General Health'] as const;
const SPECIALTIES = ['Strength Training', 'Cardio', 'Yoga', 'Nutrition', 'Recovery', 'Sports Performance'] as const;
const WEARABLES = ['Apple Health', 'Oura Ring', 'Whoop', 'Garmin', 'No Device'] as const;

interface SignUpFlowProps {
  onComplete: (userType: 'client' | 'coach') => void;
}

export function SignUpFlow({ onComplete }: SignUpFlowProps) {
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState<'client' | 'coach' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    goals: [] as string[],
    experience: '',
    specialties: [] as string[],
    philosophy: '',
    wearables: [] as string[],
  });

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const handleUserTypeSelect = (type: 'client' | 'coach') => {
    setUserType(type);
    setStep(2);
  };

  const handleComplete = () => {
    if (userType) {
      onComplete(userType);
    }
  };

  const handleStep2Continue = () => {
    const nextErrors: { name?: string; email?: string } = {};
    if (!formData.name.trim()) nextErrors.name = 'Please enter your name';
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email))
      nextErrors.email = 'Please enter a valid email';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setStep(3);
    }
  };

  if (step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-secondary/20">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-semibold">Welcome to Coach'd</h1>
            <p className="text-muted-foreground">AI-enhanced fitness coaching platform</p>
          </div>

          <div className="space-y-4">
            <Card
              className="cursor-pointer transition-all hover:shadow-lg border-2 hover:border-primary/50"
              onClick={() => handleUserTypeSelect('client')}
            >
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">I'm a Client</h3>
                  <p className="text-sm text-muted-foreground">
                    Looking for personalized fitness coaching with AI assistance
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer transition-all hover:shadow-lg border-2 hover:border-primary/50"
              onClick={() => handleUserTypeSelect('coach')}
            >
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">I'm a Coach</h3>
                  <p className="text-sm text-muted-foreground">Want to enhance my coaching with AI-powered tools</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>{userType === 'client' ? 'Complete Your Profile' : 'Coach Setup'}</CardTitle>
            <div className="w-full bg-secondary rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step - 1) * 50}%` }}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    autoComplete="name"
                    aria-invalid={!!errors.name}
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter your email"
                  />
                  {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                </div>

                {userType === 'client' && (
                  <div className="space-y-2">
                    <Label>Primary Goals</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {GOALS.map((goal) => (
                        <div key={goal} className="flex items-center space-x-2">
                          <Checkbox
                            id={goal}
                            checked={formData.goals.includes(goal)}
                            onCheckedChange={(checked) => {
                              const isChecked = checked === true;
                              setFormData((prev) => ({
                                ...prev,
                                goals: isChecked
                                  ? Array.from(new Set([...prev.goals, goal]))
                                  : prev.goals.filter((g) => g !== goal),
                              }));
                            }}
                          />
                          <Label htmlFor={goal} className="text-sm">
                            {goal}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {userType === 'coach' && (
                  <div className="space-y-2">
                    <Label htmlFor="experience">Experience Level</Label>
                    <Select
                      value={formData.experience}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, experience: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select experience level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entry">Entry Level (0-2 years)</SelectItem>
                        <SelectItem value="intermediate">Intermediate (2-5 years)</SelectItem>
                        <SelectItem value="experienced">Experienced (5-10 years)</SelectItem>
                        <SelectItem value="expert">Expert (10+ years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button onClick={handleStep2Continue}>Continue</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {userType === 'coach' && (
                  <>
                    <div className="space-y-2">
                      <Label>Specialties</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {SPECIALTIES.map((specialty) => (
                          <div key={specialty} className="flex items-center space-x-2">
                            <Checkbox
                              id={specialty}
                              checked={formData.specialties.includes(specialty)}
                              onCheckedChange={(checked) => {
                                const isChecked = checked === true;
                                setFormData((prev) => ({
                                  ...prev,
                                  specialties: isChecked
                                    ? Array.from(new Set([...prev.specialties, specialty]))
                                    : prev.specialties.filter((s) => s !== specialty),
                                }));
                              }}
                            />
                            <Label htmlFor={specialty} className="text-sm">
                              {specialty}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="philosophy">Coaching Philosophy</Label>
                      <Textarea
                        id="philosophy"
                        value={formData.philosophy}
                        onChange={(e) => setFormData((prev) => ({ ...prev, philosophy: e.target.value }))}
                        placeholder="Describe your approach to coaching..."
                        className="min-h-20"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Connect Wearables (Optional)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {WEARABLES.map((device) => (
                      <div key={device} className="flex items-center space-x-2">
                        <Checkbox
                          id={device}
                          checked={formData.wearables.includes(device)}
                          onCheckedChange={(checked) => {
                            const isChecked = checked === true;
                            setFormData((prev) => ({
                              ...prev,
                              wearables: isChecked
                                ? Array.from(new Set([...prev.wearables, device]))
                                : prev.wearables.filter((w) => w !== device),
                            }));
                          }}
                        />
                        <Label htmlFor={device} className="text-sm">
                          {device}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Connect your devices to get personalized AI recommendations
                  </p>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button onClick={handleComplete}>Complete Setup</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
